'use client'

import { usePathname } from 'next/navigation'
import { motion } from 'motion/react'

import { BlockLink } from '@/components/BlockLink'
import { CMSLinkType, resolveCMSLinkHref } from '@/components/Link'
import { RenderLink } from '@/heros/HeroCenter'

import type { ContactLinksBlock as ContactLinksBlockType } from '@/payload-types'

type ContactLinkType = {
  subtitle?: string
  background: boolean
  type: 'block' | 'plain'
  contactType: 'internal' | 'external' | 'email' | 'phone' | 'address' | 'none' | 'formTrigger'
  link: CMSLinkType
}

// --- Helpers (pure, outside component to avoid re-creation to prevent re-rendering) ---

function getPlainLinkHref(link: ContactLinkType): string | undefined {
  switch (link.contactType) {
    case 'external':
    case 'address':
      return link.link.url ?? undefined
    case 'email':
      return link.link.url ? `mailto:${link.link.url}` : undefined
    case 'phone':
      return link.link.url ? `tel:${link.link.url}` : undefined
    default:
      return undefined
  }
}

// --- Animation variants (static) ---

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1,
    },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut' as const,
    },
  },
}

// --- Sub-components (outside main component to prevent re-rendering) ---

const PlainLinkItem: React.FC<{ link: ContactLinkType }> = ({ link }) => {
  const href = getPlainLinkHref(link)
  return (
    <div className="w-full self-stretch p-2 text-primary md:p-4">
      <p className="text-sm leading-5 font-normal text-muted-foreground">{link.link.label}</p>
      {link.contactType === 'internal' ? (
        <RenderLink link={link.link} variant="icon" className="size-6 text-primary" color="none">
          {link.link.label}
        </RenderLink>
      ) : (
        href &&
        (link.contactType === 'address' ? (
          <p className="text-sm text-primary">{link.link.url}</p>
        ) : (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-primary transition-colors hover:text-primary/80"
          >
            {link.link.url}
          </a>
        ))
      )}
    </div>
  )
}

type ContactLinkItemProps = {
  link: ContactLinkType
  pathname: string
}

const ContactLinkItem: React.FC<ContactLinkItemProps> = ({ link, pathname }) => {
  if (link.type === 'plain') {
    return <PlainLinkItem link={link} />
  }

  // Form triggers append the URL (e.g. a hash) to the current pathname
  const href =
    link.contactType === 'formTrigger'
      ? `${pathname}${link.link.url ?? ''}`
      : resolveCMSLinkHref(link.link)

  return (
    <BlockLink
      href={href}
      title={link.link.label ?? ''}
      subtitle={link.subtitle ?? ''}
      isInternalLink={link.contactType === 'internal'}
      newTab={link.link.newTab ?? false}
    />
  )
}

// --- Main block component ---

export const ContactLinksBlock: React.FC<ContactLinksBlockType> = ({ heading, links }) => {
  const pathname = usePathname()

  return links && links.length > 0 ? (
    <div>
      {heading && <h2 className="mb-4 font-body text-2xl font-semibold">{heading}</h2>}
      <motion.div
        className="grid grid-cols-1 gap-4 md:grid-cols-2"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        {links.map((link) => {
          const contactLink = link.cLink as ContactLinkType
          return (
            <motion.div key={link.id} variants={itemVariants}>
              <ContactLinkItem link={contactLink} pathname={pathname} />
            </motion.div>
          )
        })}
      </motion.div>
    </div>
  ) : null
}
